import React, { useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const toIsoDate = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const pickerSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: 30,
    height: 30,
    borderRadius: '6px',
    fontSize: 12,
    fontFamily: 'inherit',
    fontWeight: 500,
    background: 'var(--chatbot-paper, var(--qv-bg-ffffff, #fff))',
    color: 'var(--chatbot-ink, var(--qv-tx-253338, #253338))',
    cursor: 'pointer',
  },
  '& .MuiOutlinedInput-input': {
    padding: '4px 6px',
    fontSize: 12,
    fontFamily: 'inherit',
    fontWeight: 500,
    letterSpacing: 'normal',
    textTransform: 'none',
    cursor: 'pointer',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--chatbot-border, var(--qv-bd-e5e7eb, #e5e7eb))',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--chatbot-border-strong, var(--qv-bd-d1d5db, #d1d5db))',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--chatbot-primary, #0a64f9)',
    borderWidth: '1px',
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    padding: 2,
    color: 'var(--chatbot-text-muted, #6b7280)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
};

const isPickerNavigationKey = (key) =>
  key === 'Tab' || key === 'Escape' || key === 'Enter';

const blockKeyboardEdit = (event) => {
  if (isPickerNavigationKey(event.key)) return;
  event.preventDefault();
};

/**
 * Compact chatbot PO date field — always displays MM/DD/YYYY,
 * stores ISO YYYY-MM-DD for the API.
 */
const ChatbotPoDateField = ({
  value,
  onChange,
  disabled = false,
  minDate = null,
  allowClear = false,
  'aria-label': ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const pickerValue =
    value && dayjs(value).isValid() ? dayjs(value) : null;
  const minPickerDate =
    minDate && dayjs(minDate).isValid() ? dayjs(minDate) : undefined;

  const openPicker = () => {
    if (!disabled) setOpen(true);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={pickerValue}
        onChange={(next) => {
          if (!next || !dayjs(next).isValid()) {
            onChange(allowClear ? '' : toIsoDate(pickerValue));
            return;
          }
          onChange(toIsoDate(next));
        }}
        disabled={disabled}
        format="MM/DD/YYYY"
        minDate={minPickerDate}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        sx={pickerSx}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            placeholder: 'MM/DD/YYYY',
            readOnly: true,
            inputProps: {
              'aria-label': ariaLabel,
              readOnly: true,
            },
            onClick: openPicker,
            onKeyDown: blockKeyboardEdit,
            onKeyPress: blockKeyboardEdit,
            onPaste: (event) => event.preventDefault(),
            onCut: (event) => event.preventDefault(),
          },
          actionBar: allowClear
            ? { actions: ['clear', 'cancel', 'accept'] }
            : { actions: ['cancel', 'accept'] },
          dialog: {
            'data-chatbot-overlay': 'true',
          },
          popper: {
            'data-chatbot-overlay': 'true',
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default ChatbotPoDateField;
